import { useState, type ReactNode } from "react"

import { ArrowPathIcon } from "@heroicons/react/20/solid"
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/solid"

import type {
  SolarHdmAvlKind,
  SolarHdmDto,
  SolarHdmOptionDto,
  SolarHdmProjectType,
  SolarHdmQuoteDto,
  SolarHdmSurveyLanguage,
} from "~/api/dto/solar-proposal-hdm.dto"
import useCreateSolarHdmQuote from "~/api/hooks/useCreateSolarHdmQuote"
import useSolarProposalHdm from "~/api/hooks/useSolarProposalHdm"
import {
  Badge,
  ComboboxField,
  SelectField,
  TextField,
  ToggleField,
} from "~/components/ui-components/v2"
import { parseAxiosError } from "~/utils/parseAxiosError"

import {
  HDM_SURVEY_LANGUAGES,
  solarHdmDraftFromForm,
  toSolarHdmForm,
  withProjectType,
  WITH_STORAGE,
  type SolarHdmForm,
  type SolarHdmFormErrors,
  type SolarHdmNumberField,
  type SolarHdmTextField,
} from "./solar-hdm-form"

export const HDM_CREATE_LABEL = `Create HDM quote`
const HDM_CREATE_BUSY_LABEL = `Creating quote…`
const HDM_CREATE_ERROR_PROMPT = `HDM couldn't create the quote. Try again.`
export const HDM_CONFIRM_LABEL = `I've confirmed these details. They're sent to HDM only and don't change this proposal.`

const usd = (value: number | null): string =>
  value == null
    ? `—`
    : value.toLocaleString(`en-US`, { style: `currency`, currency: `USD` })

const SYSTEM_LOCATION_LABEL = (value: string): string => value.replace(/_/g, ` `)

const AVL_FIELDS: readonly {
  kind: SolarHdmAvlKind
  field: `batteryAvlId` | `inverterAvlId` | `moduleAvlId` | `rackingAvlId`
  label: string
}[] = [
  { kind: `module`, field: `moduleAvlId`, label: `Module` },
  { kind: `inverter`, field: `inverterAvlId`, label: `Inverter` },
  { kind: `racking`, field: `rackingAvlId`, label: `Racking` },
  { kind: `battery`, field: `batteryAvlId`, label: `Battery` },
]

const Section = ({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: ReactNode
}): JSX.Element => (
  <section
    aria-label={title}
    className="space-y-4 rounded-xl border border-border bg-surface p-5"
  >
    <div className="space-y-1">
      <h3 className="text-xs font-semibold tracking-wide text-muted uppercase">
        {title}
      </h3>
      {description ? <p className="text-sm text-muted">{description}</p> : null}
    </div>
    {children}
  </section>
)

const Notice = ({
  tone,
  children,
  testId,
}: {
  tone: `info` | `success` | `warning`
  children: ReactNode
  testId?: string
}): JSX.Element => {
  const Icon =
    tone === `success`
      ? CheckCircleIcon
      : tone === `warning`
        ? ExclamationTriangleIcon
        : InformationCircleIcon
  const color =
    tone === `success`
      ? `border-success/40 bg-success/10 [&>svg]:text-success`
      : tone === `warning`
        ? `border-warning/40 bg-warning/10 [&>svg]:text-warning`
        : `border-border bg-surface [&>svg]:text-muted`
  return (
    <div
      role={tone === `warning` ? `alert` : `status`}
      data-testid={testId}
      className={`flex items-start gap-2 rounded-lg border p-3 text-sm text-foreground ${color}`}
    >
      <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <div className="space-y-1">{children}</div>
    </div>
  )
}

const QuoteList = ({
  quotes,
}: {
  quotes: readonly SolarHdmQuoteDto[]
}): JSX.Element => (
  <Section title="HDM quotes">
    <ul className="space-y-3" data-testid="solar-hdm-quotes">
      {quotes.map((quote) => (
        <li
          key={quote.id}
          className="flex flex-col gap-1 rounded-lg border border-border p-3 text-sm"
        >
          <span className="flex flex-wrap items-center gap-2 font-semibold text-foreground">
            Quote {quote.hdmQuoteId}
            {quote.testRecord ? <Badge tone="yellow">Test record</Badge> : null}
          </span>
          <span className="text-muted">
            {quote.projectType}
            {quote.systemSizeKw != null ? ` · ${quote.systemSizeKw} kW` : ``}
            {` · `}
            {usd(quote.grandTotalPrice)} total, {usd(quote.grandTotalPrepay)}
            {` `}
            prepay
          </span>
          <span className="text-muted">
            Created {new Date(quote.createdAt).toLocaleString()}
            {quote.createdByName ? ` by ${quote.createdByName}` : ``}
          </span>
        </li>
      ))}
    </ul>
  </Section>
)

/**
 * The quote form. It starts from the proposal and HDM's catalog; every edit
 * changes only what HDM receives.
 */
const HdmQuoteForm = ({
  proposalId,
  hdm,
  onCreated,
}: {
  proposalId: string
  hdm: SolarHdmDto & {
    draft: NonNullable<SolarHdmDto[`draft`]>
    options: NonNullable<SolarHdmDto[`options`]>
  }
  onCreated: (quote: SolarHdmQuoteDto) => void
}): JSX.Element => {
  const { options } = hdm
  const [form, setForm] = useState<SolarHdmForm>(() => toSolarHdmForm(hdm.draft))
  const [errors, setErrors] = useState<SolarHdmFormErrors>({})
  const [confirmed, setConfirmed] = useState(false)
  const create = useCreateSolarHdmQuote()
  const disabled = !hdm.viewerCanCreate || create.isLoading
  const storage = form.projectType === WITH_STORAGE

  const set = <K extends keyof SolarHdmForm>(
    key: K,
    value: SolarHdmForm[K],
  ): void => {
    setForm((current) => ({ ...current, [key]: value }))
    setErrors((current) => ({ ...current, [key]: undefined }))
    setConfirmed(false)
  }

  const text = (
    field: SolarHdmNumberField | SolarHdmTextField,
    label: string,
    extra: {
      inputMode?: `decimal` | `email` | `tel`
      description?: string
    } = {},
  ): JSX.Element => (
    <TextField
      label={label}
      name={field}
      disabled={disabled}
      value={form[field]}
      inputMode={extra.inputMode}
      description={extra.description}
      error={errors[field]}
      onChange={(event) => set(field, event.target.value)}
    />
  )

  const option = (
    list: readonly SolarHdmOptionDto[],
    id: string | null,
  ): SolarHdmOptionDto | null => list.find((entry) => entry.id === id) ?? null

  const submit = (): void => {
    const result = solarHdmDraftFromForm(form)
    if (result.errors) {
      setErrors(result.errors)
      return
    }
    create.mutate(
      { proposalId, draft: result.draft },
      {
        onSuccess: (quote) => onCreated(quote),
        // HDM may have created the quote anyway (it said so, or never
        // answered), so a retry is a fresh, deliberate confirmation.
        onError: () => setConfirmed(false),
      },
    )
  }

  return (
    <form
      className="space-y-5"
      data-testid="solar-hdm-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <Section title="Homeowner">
        <div className="grid gap-4 sm:grid-cols-2">
          {text(`customerFirstName`, `First name`)}
          {text(`customerLastName`, `Last name`)}
          {text(`email`, `Email`, { inputMode: `email` })}
          {text(`phoneNumber`, `Phone`, { inputMode: `tel` })}
        </div>
      </Section>

      <Section
        title="Property"
        description={
          hdm.eligibility
            ? [
                hdm.eligibility.addressVerified
                  ? `HDM verified this address.`
                  : `HDM couldn't verify this address.`,
                hdm.eligibility.energyCommunityEligible
                  ? `Energy Community${hdm.eligibility.energyCommunityName ? `: ${hdm.eligibility.energyCommunityName}` : ``}.`
                  : `Not in an Energy Community.`,
                `HDM checks the address again when the quote is created.`,
              ].join(` `)
            : undefined
        }
      >
        {text(`street`, `Street`)}
        <div className="grid gap-4 sm:grid-cols-2">
          {text(`city`, `City`)}
          {text(`zip`, `ZIP code`)}
        </div>
      </Section>

      <Section title="Utility & usage">
        <ComboboxField<SolarHdmOptionDto>
          label="Utility (HDM)"
          portal={false}
          disabled={disabled}
          options={options.utilities}
          value={option(options.utilities, form.utilityId)}
          placeholder="Select the homeowner's utility"
          getKey={(entry) => entry.id}
          getLabel={(entry) => entry.name}
          error={errors.utilityId}
          onChange={(entry) => entry && set(`utilityId`, entry.id)}
          emptyMessage="No HDM utilities match that search."
        />
        {text(`annualUsageKwh`, `Annual usage (kWh)`, { inputMode: `decimal` })}
      </Section>

      <Section title="System">
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField<SolarHdmProjectType>
            label="Project type"
            portal={false}
            disabled={disabled}
            options={options.projectTypes}
            value={form.projectType}
            getKey={(value) => value}
            getLabel={(value) => value}
            onChange={(value) => {
              // The grand totals add the battery in or out with the type.
              setForm((current) => withProjectType(current, value))
              setErrors((current) => ({
                ...current,
                grandTotalPrice: undefined,
                grandTotalPrepay: undefined,
              }))
              setConfirmed(false)
            }}
          />
          <SelectField<string>
            label="System location"
            portal={false}
            disabled={disabled || options.systemLocations.length === 0}
            options={options.systemLocations}
            value={form.systemLocation}
            placeholder="Select a location"
            getKey={(value) => value}
            getLabel={SYSTEM_LOCATION_LABEL}
            onChange={(value) => set(`systemLocation`, value)}
          />
          {text(`systemSizeKw`, `System size (kW)`, { inputMode: `decimal` })}
          {text(`year1ProductionKwh`, `Year-1 production (kWh)`, {
            inputMode: `decimal`,
          })}
          {text(`initialTermKwh`, `Initial term (kWh)`, {
            inputMode: `decimal`,
          })}
        </div>
        <ToggleField
          label="Domestic content (PPA)"
          disabled={disabled}
          checked={form.domesticContentPpa}
          onChange={(checked: boolean) => set(`domesticContentPpa`, checked)}
        />
      </Section>

      <Section
        title="Equipment (HDM AVL)"
        description="Prefilled from each item's PowurOS catalog number."
      >
        {AVL_FIELDS.filter(({ kind }) => storage || kind !== `battery`).map(
          ({ kind, field, label }) => (
            <ComboboxField<SolarHdmOptionDto>
              key={field}
              label={label}
              portal={false}
              disabled={disabled}
              options={options.avl[kind]}
              value={option(options.avl[kind], form[field])}
              placeholder={`Select the ${label.toLowerCase()} on HDM's AVL`}
              getKey={(entry) => entry.id}
              getLabel={(entry) => entry.name}
              onChange={(entry) => set(field, entry?.id ?? null)}
              emptyMessage="No AVL entries match that search."
            />
          ),
        )}
      </Section>

      {storage ? (
        <Section title="Battery">
          <div className="grid gap-4 sm:grid-cols-2">
            {text(`batteryKwSize`, `Battery size (kWh)`, {
              inputMode: `decimal`,
            })}
            {text(`batteryCostPerKw`, `Cost per kWh ($)`, {
              inputMode: `decimal`,
            })}
            {text(`batterySalesPrice`, `Sales price ($)`, {
              inputMode: `decimal`,
            })}
            {text(`batteryPrepaid`, `Prepaid ($)`, { inputMode: `decimal` })}
          </div>
          <ToggleField
            label="Domestic content (ESSA)"
            disabled={disabled}
            checked={form.domesticContentEssa}
            onChange={(checked: boolean) => set(`domesticContentEssa`, checked)}
          />
        </Section>
      ) : null}

      <Section
        title="Pricing"
        description="Prepay amounts take the HDM discount off each price."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {text(`sowPrice`, `Solar (SOW) price ($)`, { inputMode: `decimal` })}
          {text(`sowPerW`, `SOW price per watt ($)`, { inputMode: `decimal` })}
          {text(`solarPrepayAmount`, `Solar prepay ($)`, {
            inputMode: `decimal`,
          })}
          {text(`grandTotalPrice`, `Grand total ($)`, { inputMode: `decimal` })}
          {text(`grandTotalPrepay`, `Grand total prepay ($)`, {
            inputMode: `decimal`,
          })}
        </div>
      </Section>

      <Section title="Homeowner confirmation">
        <ToggleField
          label="Send HDM's confirmation survey to the homeowner"
          disabled={disabled}
          checked={form.sendConfirmationCustomer}
          onChange={(checked: boolean) =>
            set(`sendConfirmationCustomer`, checked)
          }
        />
        <div className="max-w-56">
          <SelectField<SolarHdmSurveyLanguage>
            label="Survey language"
            portal={false}
            disabled={disabled}
            options={[...HDM_SURVEY_LANGUAGES]}
            value={form.confirmationSurveyLanguage}
            getKey={(value) => value}
            getLabel={(value) => value}
            onChange={(value) => set(`confirmationSurveyLanguage`, value)}
          />
        </div>
      </Section>

      <div className="space-y-3">
        {Object.values(errors).some(Boolean) ? (
          <p role="alert" className="text-sm text-danger">
            Fix the highlighted fields before creating the quote.
          </p>
        ) : null}
        {create.isError ? (
          <p role="alert" className="text-sm text-danger">
            {parseAxiosError(create.error, HDM_CREATE_ERROR_PROMPT)}
          </p>
        ) : null}
        <label className="flex items-start gap-3 text-sm text-foreground">
          <input
            type="checkbox"
            className="mt-0.5 size-4 shrink-0 accent-primary"
            disabled={disabled}
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          {HDM_CONFIRM_LABEL}
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={disabled || !confirmed}
            className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {create.isLoading ? HDM_CREATE_BUSY_LABEL : HDM_CREATE_LABEL}
          </button>
          <button
            type="button"
            disabled={disabled}
            className="text-sm font-semibold text-muted underline hover:text-foreground disabled:opacity-50"
            onClick={() => {
              setForm(toSolarHdmForm(hdm.draft))
              setErrors({})
              setConfirmed(false)
            }}
          >
            Reset to proposal values
          </button>
        </div>
      </div>
    </form>
  )
}

/**
 * Contract › HDM. Phase one creates an HDM quote: the seller confirms or
 * changes what Vision proposes from the proposal (PowurOS catalog numbers are
 * the HDM AVL ids), and the confirmed copy goes to HDM. Quotes already
 * created are listed above the form.
 */
export const SolarHdmStep = ({
  proposalId,
}: {
  proposalId: string
}): JSX.Element => {
  const hdm = useSolarProposalHdm(proposalId)
  const [created, setCreated] = useState<SolarHdmQuoteDto | null>(null)
  const [formKey, setFormKey] = useState(0)

  // The read refetches on every open; the form seeds from this open's answer,
  // never a cached draft from before a proposal change.
  if (hdm.isLoading || (hdm.isFetching && !hdm.isFetchedAfterMount)) {
    return (
      <p className="text-sm text-muted" data-testid="solar-hdm-loading">
        Loading HDM…
      </p>
    )
  }

  if (hdm.isError || !hdm.data) {
    return (
      <Notice tone="warning" testId="solar-hdm-error">
        <p>{parseAxiosError(hdm.error, `HDM couldn't be loaded.`)}</p>
      </Notice>
    )
  }

  const { data } = hdm
  const { draft, options } = data

  return (
    <div className="space-y-5" data-testid="solar-hdm-step">
      {created ? (
        <Notice tone="success" testId="solar-hdm-created">
          <p className="font-semibold">
            HDM quote {created.hdmQuoteId} created.
          </p>
          {created.testRecord ? (
            <p>It was created as an HDM test record.</p>
          ) : null}
        </Notice>
      ) : null}

      {data.quotes.length > 0 ? <QuoteList quotes={data.quotes} /> : null}

      {!data.hdmProgramEnabled ? (
        <Notice tone="info" testId="solar-hdm-program-off">
          <p>
            The HDM Finance Program isn&apos;t enabled on this proposal, so the
            prepay amounts carry no HDM discount.
          </p>
        </Notice>
      ) : null}

      {!data.viewerCanCreate ? (
        <Notice tone="info">
          <p>You can view HDM quotes on this proposal but not create them.</p>
        </Notice>
      ) : null}

      {draft && options ? (
        created ? (
          <button
            type="button"
            className="rounded-full border border-border px-5 py-2 text-sm font-semibold text-foreground hover:bg-surface"
            onClick={() => {
              setCreated(null)
              setFormKey((key) => key + 1)
            }}
          >
            Create another quote
          </button>
        ) : (
          <>
            {data.warnings.length > 0 ? (
              <Notice tone="warning" testId="solar-hdm-warnings">
                <p className="font-semibold">
                  Check these before creating the quote:
                </p>
                <ul className="list-disc space-y-1 pl-4">
                  {data.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </Notice>
            ) : null}
            <HdmQuoteForm
              key={formKey}
              proposalId={proposalId}
              hdm={{ ...data, draft, options }}
              onCreated={setCreated}
            />
          </>
        )
      ) : (
        <Notice tone="warning" testId="solar-hdm-unavailable">
          <p>{data.unavailableReason ?? `HDM isn't available right now.`}</p>
          {data.configured ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 font-semibold text-primary underline disabled:opacity-50"
              disabled={hdm.isFetching}
              onClick={() => void hdm.refetch()}
            >
              <ArrowPathIcon aria-hidden="true" className="size-4" />
              {hdm.isFetching ? `Retrying…` : `Try again`}
            </button>
          ) : null}
        </Notice>
      )}
    </div>
  )
}
